import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  use,
  useEffect,
  useState,
} from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/services/fcm/fcm.service.ts';

type TNotification = {
  title: string;
  body: string;
  id: string;
  sent_at: { seconds: number; nanoseconds: number } | null;
  marked_as_read_at: { seconds: number; nanoseconds: number } | null;
  maintenance_task_id: number;
  location: {
    room_name: string;
  };
  task_details: {
    name: string;
    description: string;
    priority: string;
    warning?: string;
  };
  timing: {
    execution_date: string;
  };
};

interface INotificationContext {
  userId: number | null;
  setUserId: Dispatch<SetStateAction<number | null>>;
  notifications: TNotification[] | null;
}

const NotificationContext = createContext<INotificationContext>({
  userId: null,
  setUserId: () => {},
  notifications: null,
});

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [userId, setUserId] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<TNotification[] | null>(null);

  useEffect(() => {
    if (userId === null) return;

    const q = query(collection(db, 'notifications'), where('user_id', '==', userId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<TNotification, 'id'>),
        }))
        .filter((notify) => notify.marked_as_read_at === null)
        .sort((a, b) => b.sent_at.seconds - a.sent_at.seconds);
      setNotifications(data);
    });

    return () => unsubscribe();
  }, [userId]);

  return (
    <NotificationContext value={{ notifications, userId, setUserId }}>
      {children}
    </NotificationContext>
  );
};

export const useNotification = () => {
  return use(NotificationContext);
};
